import React, { useState } from "react";
import { useDispatch } from "react-redux";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import FormInput from "../../Ui/FormInput";
import FormSelect from "../../FormFields/FormSelect";
import ButtonField from "../../Ui/ButtonField";
import SupportRepository from "../../../Repositories/SupportRepository";
import { SHOW_ERROR, SHOW_SUCCESS } from "../../../Redux/Reducers/MessageReducers";
import { SUPPORT_SUBJECTS } from "../../../config/supportContact";
import { useTailwindBrand } from "../../shared";

interface ContactSupportFormProps {
    /** Sujet présélectionné (doit figurer dans SUPPORT_SUBJECTS). */
    defaultSubject?: string;
    /** Contexte transmis tel quel à l'équipe (ex. « Cours n°123 du 12/10/2026 »). */
    context?: string;
    /** Appelé après un envoi réussi (fermeture d'une fenêtre, par exemple). */
    onSent?: () => void;
    /** Texte d'introduction au-dessus du formulaire. */
    intro?: string;
}

const MAX_MESSAGE_LENGTH = 2000;

/**
 * Formulaire de contact de l'équipe support, commun au prof et au client :
 * sujet, message, téléphone de rappel facultatif. Réutilisable dans une page
 * ou une fenêtre (signalement depuis le détail d'un cours côté client).
 */
const ContactSupportForm: React.FC<ContactSupportFormProps> = ({ defaultSubject, context, onSent, intro }) => {
    const dispatch = useDispatch();
    const { classes, brandConfig } = useTailwindBrand();
    const [subjectIndex, setSubjectIndex] = useState<number | "">(
        defaultSubject && SUPPORT_SUBJECTS.includes(defaultSubject) ? SUPPORT_SUBJECTS.indexOf(defaultSubject) + 1 : ""
    );
    const [message, setMessage] = useState("");
    const [phone, setPhone] = useState("");
    const [errors, setErrors] = useState<{ subject?: string; message?: string }>({});
    const [sending, setSending] = useState(false);

    const validate = () => {
        const next: { subject?: string; message?: string } = {};
        if (!subjectIndex) next.subject = "Choisissez le sujet de votre demande";
        if (message.trim().length < 10) next.message = "Décrivez votre demande en quelques mots (10 caractères minimum)";
        if (message.length > MAX_MESSAGE_LENGTH) next.message = `Votre message dépasse ${MAX_MESSAGE_LENGTH} caractères`;
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate() || sending) return;
        setSending(true);
        try {
            await SupportRepository.sendContactRequest({
                subject: SUPPORT_SUBJECTS[(subjectIndex as number) - 1],
                message: message.trim(),
                phone: phone.trim() || undefined,
                context,
            });
            dispatch({ type: SHOW_SUCCESS, payload: "Votre message a bien été transmis à notre équipe. Nous revenons vers vous rapidement." });
            setMessage("");
            setPhone("");
            setSubjectIndex("");
            onSent?.();
        } catch {
            dispatch({ type: SHOW_ERROR, payload: "Votre message n'a pas pu être envoyé. Réessayez dans quelques instants ou écrivez-nous directement par mail." });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="space-y-4">
            {intro && (
                <p className={`text-sm ${classes.text} ${classes.fontFamily}`}>{intro}</p>
            )}
            <FormSelect
                label="Sujet"
                required
                data={SUPPORT_SUBJECTS}
                defaultValue={subjectIndex}
                callback={(value: number) => {
                    setSubjectIndex(value);
                    setErrors((prev) => ({ ...prev, subject: undefined }));
                }}
                error={errors.subject}
            />
            <FormInput
                label="Votre message"
                required
                textarea
                placeholder="Expliquez-nous votre demande : de quoi s'agit-il, depuis quand, ce que vous avez déjà essayé…"
                value={message}
                onChangeText={(e) => {
                    setMessage(e.target.value);
                    setErrors((prev) => ({ ...prev, message: undefined }));
                }}
                error={errors.message}
            />
            <FormInput
                label="Téléphone pour vous rappeler (facultatif)"
                type="tel"
                placeholder="Ex : 06 12 34 56 78"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
            />
            <div className="flex justify-end">
                <ButtonField
                    label="Envoyer"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={sending}
                    disabled={sending}
                    endIcon={<SendRoundedIcon fontSize="small" />}
                    style={{ fontFamily: brandConfig.fontFamily }}
                />
            </div>
        </div>
    );
};

export default ContactSupportForm;
